import { Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { DwComponent, DwComponentType, DwContextSubjects, DwMetaDataFormStateService } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ChallengeTeamEntity, DwUserEntity, UserProfileEntity } from '../../../../meta-data/app-meta-data.service';
import { AppearanceSettings } from '../../services/appearance/appearance';
import { ProfileInlineEditService } from '../../../user-profile/services/profile-inline-edit.service';
import { ProfileFormService } from '../../../user-profile/services/profile-form.service';
import { ProfileDirectoryService } from '../../services/profile-routing/profile-directory.service';

type BannerProfileData = Partial<DwUserEntity & ChallengeTeamEntity> & AppearanceSettings;

/** Shared profile header; account images remain editable by the owner and profile admins. */
@DwComponent({ key:'fitness-profile-avatar-banner', name:'Profile avatar banner', componentType:DwComponentType.formSection, isGlobal:true })
@Component({ selector:'app-profile-avatar-banner', template:`
  <app-avatar-banner *ngIf="data" mode="full" [name]="data.TeamName || ((data.FirstName || '') + ' ' + (data.LastName || '')).trim()"
    [editable]="canEditImages" (styleRequested)="editStyle()" [saveImages]="saveImages" [imageOwnerKey]="'user:' + data.UserId"
    [subtitle]="data.TeamName ? data.TeamLocation : 'Streaker profile'"
    [avatarMediaId]="data.TeamName ? data.TeamPictureMediaId : data.UserPictureMediaId"
    [bannerMediaId]="data.TeamName ? data.TeamBackgroundMediaId : data.UserBackgroundMediaId"
    [appearance]="profile || (data.UserProfile && data.UserProfile[0]) || data">

  </app-avatar-banner><p *ngIf="error" role="alert">{{ error }}</p>` })
export class ProfileAvatarBannerComponent implements OnDestroy {
    @Input() sectionConfig: { config?: { editable?: boolean; participantView?: boolean }; isReadOnly?: boolean };
    data: BannerProfileData;
    profile: UserProfileEntity;
    editing = false;
    error = '';
    private subscription = new Subscription();
    private _focusTimer: ReturnType<typeof setTimeout>;
    private appearanceRequest: Subscription;
    saveImages = (avatarId: number, bannerId: number) => {
        const userId = this.data.UserId;
        return this.forms.saveImages(userId, avatarId, bannerId).pipe(tap(() => {
            if (this.data?.UserId === userId) {
                this.inline.completed$.next({userId, cancelled: false, data: {
                    ...this.data, UserId: userId, UserPictureMediaId: avatarId, UserBackgroundMediaId: bannerId,
                } as DwUserEntity});
            }
        }));
    };
    constructor(private forms: ProfileFormService, private directory: ProfileDirectoryService,
        private inline: ProfileInlineEditService, private _element: ElementRef<HTMLElement>,
        private _formState: DwMetaDataFormStateService) {}
    get canEdit(): boolean { return this.sectionConfig?.config?.editable === true && !this.sectionConfig?.isReadOnly && this.forms.canEdit(this.data?.UserId); }
    // Avatar and banner belong to the account, regardless of challenge or section read-only state.
    get canEditImages(): boolean { return this.forms.canEdit(this.data?.UserId); }
    @Input() set context(value: DwContextSubjects) {
        clearTimeout(this._focusTimer);
        this.subscription.unsubscribe(); this.subscription = new Subscription(); this.data = null;
        this.appearanceRequest?.unsubscribe(); this.profile = null; this.error = '';
        this.subscription.add(this.inline.userId$.subscribe(userId => this.editing = userId === this.data?.UserId));
        this.subscription.add(this.inline.bannerAction$.subscribe(command => {
            if (command.userId !== this.data?.UserId) { return; }
            if (command.action === 'edit') { this.editProfile(); } else { this.editStyle(); }
        }));
        this.subscription.add(this.inline.completed$.subscribe(result => {
            if (result.userId !== this.data?.UserId) { return; }
            if (!result.cancelled && result.data?.UserId === result.userId) {
                const savedProfile = Array.isArray(result.data.UserProfile) ? result.data.UserProfile[0] : result.data.UserProfile;
                const updated = { ...this.data, ...result.data, ...(savedProfile || {}) };
                // Keep CAP's data, original-data and loading/security lifecycle in sync.
                // Publishing directly to the context bypasses the owning form's update pipeline.
                this._formState.state.formApi.patchFormData(updated);
                this.directory.invalidate();
            }
            clearTimeout(this._focusTimer);
            this._focusTimer = setTimeout(() => this._element.nativeElement.querySelector<HTMLButtonElement>('.actions button')?.focus(), 350);
        }));
        if (value?.data) { this.subscription.add(value.data.subscribe(data => {
            this.data = data; this.editing = this.inline.userId$.value === data?.UserId; this.loadAppearance();
        })); }
    }
    private loadAppearance(): void {
        this.appearanceRequest?.unsubscribe(); this.profile = null;
        if (!this.data?.UserId || !this.forms.canEdit(this.data.UserId)) { return; }
        this.appearanceRequest = this.forms.loadOwnedProfile(this.data.UserId).subscribe(profile => this.profile = profile,
            () => this.error = 'Unable to load your style. Reload your profile to try again.');
    }
    editProfile(): void {
        if (!this.canEdit || this.editing) { return; }
        this.error = '';
        this.inline.begin(this.data.UserId);
    }
    editStyle(): void {
        if (!this.canEditImages || this.editing || !this.profile) { return; }
        const profileId = this.profile.UserProfileId, userId = this.data.UserId;
        this.editing = true; this.error = '';
        this.subscription.add(this.forms.style(this.profile, this.data).pipe(take(1)).subscribe(result => {
            this.editing = false;
            if (!result.cancelled && result.data?.UserProfileId === profileId && this.data?.UserId === userId) {
                this.profile = { ...this.profile, ...result.data }; this.directory.invalidate();
            }
        }, () => { this.editing = false; this.error = 'Unable to open your style picker. Please try again.'; }));
    }
    ngOnDestroy(): void {
        clearTimeout(this._focusTimer); this.subscription.unsubscribe(); this.appearanceRequest?.unsubscribe();
    }
}
